import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  Mail,
  Lock,
  User,
  Phone,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Shield,
  Zap,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCountries } from "@/store/actions/countryActions";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { toast } from "sonner";
import { CustomSelect } from "@/components/shared/CustomSelect";
import { Logo } from "@/components/Logo";

// Google Icon Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Apple Icon Component
const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74s2.57-1.04 4.15-.88c.7.03 2.67.26 3.93 2.1-.07.04-2.35 1.37-2.33 4.08.02 3.26 2.85 4.34 2.88 4.35-.02.1-.45 1.52-1.49 3.06-.9 1.3-1.83 2.6-3.22 2.52zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState<
    "google" | "apple" | null
  >(null);

  const { countries } = useAppSelector((state) => state.country);

  // Step 1
  const [selectedCountry, setSelectedCountry] = useState<any>(null);

  // Step 2 fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Step 3 fields
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToMarketing, setAgreedToMarketing] = useState(false);

  const passwordStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = () => {
    const strength = passwordStrength();
    if (strength === 0) return "";
    if (strength === 1) return "Weak";
    if (strength === 2) return "Fair";
    if (strength === 3) return "Good";
    return "Strong";
  };

  const getPasswordStrengthColor = () => {
    const strength = passwordStrength();
    if (strength <= 1) return "bg-red-500";
    if (strength === 2) return "bg-yellow-500";
    if (strength === 3) return "bg-blue-500";
    return "bg-emerald-500";
  };

  const canProceedToStep2 = !!selectedCountry;

  const canProceedToStep3 =
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0;

  const canCompleteRegistration =
    password.length >= 8 && password === confirmPassword && agreedToTerms;

  const handleSocialSignup = async (provider: "google" | "apple") => {
    setIsSocialLoading(provider);

    // toast({
    //   title: "Account Created!",
    //   description: `Welcome to BUYFIAT! Your account has been created with ${provider === "google" ? "Google" : "Apple"}.`,
    // });

    setIsSocialLoading(null);
    navigate("/dashboard");
  };

  // ONLY showing the parts that changed (everything else remains exactly as you wrote)

  const handleContinue = () => {
    setCurrentStep((prev) => {
      if (prev === 1 && canProceedToStep2) return 2;
      if (prev === 2 && canProceedToStep3) return 3;
      return prev;
    });
  };

  const handleCountryChange = (option: any) => {
    setSelectedCountry(option?.value ?? null);
  };

  const handleBack = () => {
    setCurrentStep((prev) => {
      if (prev === 1) return 1;
      if (prev === 2) return 1;
      return 2; // prev === 3
    });
  };

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  useEffect(() => {
    if (currentStep === 2 && !canProceedToStep2) {
      setCurrentStep(1);
    }

    if (currentStep === 3 && !canProceedToStep3) {
      setCurrentStep(2);
    }
  }, [currentStep, canProceedToStep2, canProceedToStep3]);

  const options = countries.map((country: any) => {
    return {
      value: country.id,
      label: country.name,
      iso2: country.iso2,
    };
  });

  const formatOptionLabel = (option: any) => {
    const iso = option.iso2?.toLowerCase();

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={`https://flagcdn.com/w40/${iso}.png`}
          alt={option.label}
          width={20}
          height={14}
          style={{ objectFit: "cover", borderRadius: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!canCompleteRegistration) return;

    const data = {
      email,
      firstName,
      lastName,
      countryId: selectedCountry,
      passwordHash: password,
    };

    setIsLoading(true);
    try {
      const res = await makeRequest.post("/auth/register", data);

      if (res.data.success) {
        const payload = res.data;

        toast.success(res.data.message);

        sessionStorage.setItem(
          "otpVerification",
          JSON.stringify({
            email: res.data.email,
            issuedAt: res.data.otp.issuedAt,
            cooldown: res.data.otp.resendCooldownSeconds,
          }),
        );

        navigate("/verify-otp", {
          state: {
            payload,
          },
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
    }

    setIsLoading(false);
  };

  const renderStep1 = () => (
    <div className="flex flex-col gap-8 w-full ">
      <div className="flex flex-col gap-1">
        <Label className="text-base">Country of residence</Label>
        <CustomSelect
          options={options}
          isClearable
          isSearchable
          value={
            options.find((opt: any) => opt.value === selectedCountry) || null
          }
          onChange={handleCountryChange}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={(option: any) => option.label}
          getOptionValue={(option: any) => option.value}
        />
      </div>

      <Button
        className="w-full h-12 mt-5"
        onClick={handleContinue}
        disabled={!canProceedToStep2}
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  // Step 1: Basic Information
  const renderStep2 = () => (
    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
      <Button
        variant="ghost"
        // size="sm"
        onClick={handleBack}
        className="-ml-2 text-muted-foreground"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </Button>

      {/* Social Signup Buttons */}
      {/* <div className="space-y-3">
        <Button
          variant="outline"
          className="w-full h-11 relative font-medium"
          onClick={() => handleSocialSignup("google")}
          // disabled={isSocialLoading !== null}
          disabled={isSocialLoading !== null}
        >
          {isSocialLoading === "google" ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <span className="absolute left-4 text-red-500">
                <GoogleIcon />
              </span>
              Continue with Google
            </>
          )}
        </Button>

        <Button
          variant="outline"
          className="w-full h-11 relative font-medium"
          onClick={() => handleSocialSignup("apple")}
          disabled={isSocialLoading !== null}
        >
          {isSocialLoading === "apple" ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <span className="absolute left-4">
                <AppleIcon />
              </span>
              Continue with Apple
            </>
          )}
        </Button>
      </div> */}

      {/* <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or sign up with email
          </span>
        </div>
      </div> */}

      {/* Form Fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-base">
            Email Address
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 !h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-base">
            First Name
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="firstName"
              type="text"
              placeholder="Enter your full name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="pl-10 !h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-base">
            Last Name
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="lastName"
              type="text"
              placeholder="Enter your full name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="pl-10 !h-12"
              required
            />
          </div>
        </div>
      </div>

      <Button
        className="w-full h-11"
        onClick={handleContinue}
        disabled={!canProceedToStep2}
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  // Step 2: Password & Terms
  const renderStep3 = () => (
    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Back Button */}
      <Button
        variant="ghost"
        // size="sm"
        onClick={handleBack}
        className="-ml-2 text-muted-foreground"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </Button>

      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600">
          <Lock className="h-5 w-5 text-white" />
        </div>
        <h2 className="text-xl font-semibold">Create Password</h2>
        <p className="text-sm text-muted-foreground">
          Secure your account with a strong password
        </p>
      </div>

      {/* Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-base">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 !h-12"
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Password Strength */}
          {password && (
            <div className="space-y-2">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      i <= passwordStrength()
                        ? getPasswordStrengthColor()
                        : "bg-muted",
                    )}
                  />
                ))}
              </div>
              <p
                className={cn(
                  "text-xs",
                  passwordStrength() <= 1
                    ? "text-red-500"
                    : passwordStrength() === 2
                      ? "text-yellow-500"
                      : passwordStrength() === 3
                        ? "text-blue-500"
                        : "text-emerald-500",
                )}
              >
                {getPasswordStrengthLabel()}
              </p>
            </div>
          )}

          {/* Password Requirements */}
          <div className="space-y-1 pt-1">
            {[
              { label: "At least 8 characters", met: password.length >= 8 },
              { label: "One uppercase letter", met: /[A-Z]/.test(password) },
              { label: "One number", met: /[0-9]/.test(password) },
              {
                label: "One special character",
                met: /[^A-Za-z0-9]/.test(password),
              },
            ].map((req, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div
                  className={cn(
                    "w-4 h-4 rounded-full flex items-center justify-center transition-colors",
                    req.met
                      ? "bg-emerald-500/20 text-emerald-500"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {req.met ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-current" />
                  )}
                </div>
                <span
                  className={
                    req.met ? "text-foreground" : "text-muted-foreground"
                  }
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-base">
            Confirm Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 !h-12"
              required
            />
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-500">Passwords do not match</p>
          )}
          {confirmPassword &&
            password === confirmPassword &&
            password.length > 0 && (
              <p className="text-xs text-emerald-500 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Passwords match
              </p>
            )}
        </div>

        <div className="space-y-3 pt-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={agreedToTerms}
              onCheckedChange={(checked) =>
                setAgreedToTerms(checked as boolean)
              }
              className="mt-0.5"
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>{" "}
              and confirm I am at least 18 years old
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={agreedToMarketing}
              onCheckedChange={(checked) =>
                setAgreedToMarketing(checked as boolean)
              }
              className="mt-0.5"
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              Send me marketing updates and promotional offers (optional)
            </span>
          </label>
        </div>

        <Button
          type="submit"
          className="w-full !h-12"
          disabled={isLoading || !canCompleteRegistration}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Account...
            </>
          ) : (
            <>
              Create Account
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      {/* Security Note */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <Shield className="w-3 h-3 text-emerald-500" />
        <span>Your data is protected with 256-bit encryption</span>
      </div>
    </div>
  );

  return (
    <div className="!w-screen flex items-center justify-center bg-background p-4 mb-20">
      <div className="w-full sm:max-w-md mx-auto">
        {/* Logo Header */}
        <div className="text-center space-y-3 mb-8">
          <Logo
            iconOnly
            size="xl"
            className="transition-transform duration-300"
          />
          <h1 className="text-2xl font-bold">Create Your Account</h1>
          <p className="text-muted-foreground text-sm">
            {currentStep === 1
              ? "Start your journey with BUYFIAT"
              : "Secure your account"}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div
            className={cn(
              "h-2 w-8 rounded-full transition-colors",
              currentStep >= 1 ? "bg-primary" : "bg-muted",
            )}
          />
          <div
            className={cn(
              "h-2 w-8 rounded-full transition-colors",
              currentStep >= 2 ? "bg-primary" : "bg-muted",
            )}
          />
          <div
            className={cn(
              "h-2 w-8 rounded-full transition-colors",
              currentStep >= 3 ? "bg-primary" : "bg-muted",
            )}
          />
        </div>

        {/* Main Card */}
        <Card className="w-full border shadow-xl shadow-black/5">
          <CardContent className="p-2 sm:p-4 w-full">
            {currentStep === 1
              ? renderStep1()
              : currentStep === 2
                ? renderStep2()
                : renderStep3()}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-muted-foreground flex items-center justify-between gap-2">
            <span>Already have an account?</span>

            <Link
              to="/login"
              className="text-primary font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>

          {/* <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <Link
              to="/terms"
              className="hover:text-foreground transition-colors"
            >
              Terms
            </Link>
            <span>•</span>
            <Link
              to="/privacy"
              className="hover:text-foreground transition-colors"
            >
              Privacy
            </Link>
            <span>•</span>
            <Link
              to="/help"
              className="hover:text-foreground transition-colors"
            >
              Help
            </Link>
          </div> */}

          {/* Security Badge */}
          {/* <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span>Secure 256-bit SSL encrypted</span>
          </div> */}
        </div>
      </div>
    </div>
  );
}
